/**
 * Derive a short initials string for use in avatars / chips.
 *
 * Behaviour:
 *   - "John Doe"          -> "JD"
 *   - "John Q Public"     -> "JP"  (first + last word, skipping middles)
 *   - "Madonna"           -> "M"
 *   - "  jane   doe  "    -> "JD"  (extra whitespace tolerated)
 *   - "" / null / undef.  -> falls back to the first letter of `fallbackEmail`
 *                            (so "alice@example.com" becomes "A")
 *   - Neither available   -> "" (caller can render a placeholder)
 *
 * Non-ASCII names: we just take the first character of each picked word
 * and uppercase it. For names where uppercase has no meaning (CJK, etc.)
 * this is a no-op, which is fine for an avatar glyph.
 */
export function initialsFromName(
  fullName: string | null | undefined,
  fallbackEmail?: string | null,
): string {
  const name = (fullName ?? "").trim();
  if (name.length > 0) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    // First + last word — skip middle names for a clean two-letter glyph.
    const first = parts[0].charAt(0);
    const last = parts[parts.length - 1].charAt(0);
    return (first + last).toUpperCase();
  }

  const email = (fallbackEmail ?? "").trim();
  if (email.length > 0) {
    return email.charAt(0).toUpperCase();
  }

  return "";
}
